import { Radio, type RadioOption } from '@fukku/radio'
import { MultipleChoice } from 'product-list/components/Filters/MultipleChoice/MultipleChoice'
import { getFilterId } from 'product-list/context/ProductListFiltersProvider/utils'
import { FilterType } from 'product-list/types/Filters'
import type { OptionsProps } from 'product-list/types/Options'
import { dataTestIds } from 'product-list/utils/testIds'
import { type ReactNode, useCallback, useMemo } from 'react'
import styles from './Options.module.scss'

export function Options({
	userFilter,
	onChange = () => {},
	disabledValues,
	a11yLabel,
}: Readonly<OptionsProps>): ReactNode {
	const { options, type, multipleChoice, selection } = userFilter

	const controlId = getFilterId(userFilter)
	const radioOptions = useMemo<RadioOption[]>(
		() =>
			options.map(({ id, label }) => ({
				value: id,
				disabled: disabledValues.includes(id),
				content: <span className={styles['label-value']}>{label}</span>,
			})),
		[disabledValues, options]
	)

	const handleChange = useCallback(
		(id: string) => {
			onChange(
				type,
				id,
				userFilter.type === FilterType.generic ? userFilter.id : undefined
			)
		},
		[onChange, type, userFilter]
	)

	const handleRadioChange = useCallback(
		(valueId: string | number) => {
			handleChange(valueId.toString())
		},
		[handleChange]
	)

	if (multipleChoice) {
		return (
			<MultipleChoice
				a11yLabel={a11yLabel}
				options={options}
				disabledValues={disabledValues}
				selectedValues={userFilter.selection}
				onChange={handleChange}
			/>
		)
	}

	return (
		<Radio
			className={styles.radios}
			legend={a11yLabel}
			id={controlId}
			data-testid={dataTestIds.plpFilterType(type)}
			name={type}
			defaultSelected={selection[0]}
			onChange={handleRadioChange}
			options={radioOptions}
		/>
	)
}
